import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react'
import { useSessionStorage } from 'usehooks-ts'
import { type Entry } from 'contentful'
import { usePage } from '@inertiajs/react'

import Link from '@/components/link-component'
import Disclosure from '@/components/ui/disclosure'

// import BwiPlus from '../icons/bwi/plus-square.svg'
// import BwiMinus from '../icons/bwi/minus-square.svg'
import { isShallowEqual, removeAtIndex } from '@/utils/array'
import { useTranslations } from '@/utils/locale'
import { cx } from '@/utils/strings'
import {
  isTypeHelpArticle,
  isTypeHelpCategory,
  isTypeHelpNav,
  isTypeHelpSubcategory,
  TypeHelpArticleSkeleton,
  TypeHelpCategorySkeleton,
  TypeHelpNavSkeleton,
  TypeHelpSubcategorySkeleton,
} from '@/types/ctf'

type HelpNavProps = {
  helpNav: Entry<TypeHelpArticleSkeleton | TypeHelpNavSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>[]
  helpNavInitial: {
    index: number
    title: string
  }[]
  indexLink?: {
    title: string
    slug: string
  }
  currentTopNav: string
}

export default function HelpNav({ helpNav, helpNavInitial, indexLink, currentTopNav }: HelpNavProps) {
  const { t } = useTranslations()
  if (!indexLink) {
    indexLink = {
      title: t('Home'),
      slug: '',
    }
  }
  // help remember state between page loads
  const helpNavRef = useRef<HTMLDivElement | null>(null)
  const [openNavPaths, setOpenNavPaths] = useSessionStorage<number[][]>(`navPaths-${currentTopNav}`, [])

  const initialPath = useMemo(() => helpNavInitial.map((item) => item.index).slice(1), [helpNavInitial])
  useEffect(() => {
    if (initialPath?.length > 0) {
      // Add all initial subpaths to open paths
      const paths = initialPath.reduce<number[][]>((acc, _, i) => {
        const newPath = initialPath.slice(0, i + 1)
        acc.push(newPath)
        return acc
      }, [])
      setOpenNavPaths((prev) => {
        paths.forEach((path) => {
          // Check if path already exists
          if (!prev.some((p) => isShallowEqual(p, path))) {
            prev = prev.concat([path])
          }
        })
        return prev
      })
    }
  }, [initialPath])

  const [scrollY, setScrollY] = useSessionStorage<number>('helpNavScrollY', 0)
  const HelpLink = useCallback(
    ({
      className,
      children,
      slug = null,
      indent = true,
    }: {
      className?: string
      children: ReactNode
      slug?: string | null
      indent?: boolean
    }) => {
      const page = usePage()
      const pathname = page.url.split('#')[0]
      const path = !slug ? '/help/' : `/help/${slug}/`

      return (
        <Link
          to={path}
          className={cx(
            'text-sm xl:text-base text-primaryBlue py-1 rounded-md leading-tight w-full block hover:bg-secondary-100 dark:hover:bg-secondary-700',
            indent ? 'px-4' : 'px-2',
            pathname === path && 'bg-secondary-100 dark:bg-secondary-700',
            className
          )}
          onClick={() => setScrollY(helpNavRef?.current?.scrollTop ?? 0)}
        >
          {children}
        </Link>
      )
    },
    []
  )

  const disclosureClickHandler = (path: number[], isOpen: boolean) => {
    setOpenNavPaths((prev) => {
      if (!isOpen) {
        // Remove path
        const existingIndex = prev.findIndex((p) => isShallowEqual(p, path))
        if (existingIndex !== -1) {
          return removeAtIndex(prev, existingIndex)
        }
        return prev
      } else {
        // Add path
        if (prev.some((p) => isShallowEqual(p, path))) {
          // Path already exists
          return prev
        }
        return prev.concat([path])
      }
    })
  }

  const isIndexOpen = (path: number[]) => {
    return openNavPaths.some((p) => isShallowEqual(p, path))
  }

  useEffect(() => {
    window.addEventListener('beforeunload', () => {
      sessionStorage.removeItem('helpNavIndexes')
      sessionStorage.removeItem('helpNavScrollY')
    })
    if (helpNavRef?.current?.scrollTo && scrollY > 0) (helpNavRef.current as HTMLElement).scrollTo(0, scrollY)
  }, [])

  const renderHelpLinks = (
    item:
      | Entry<
          TypeHelpArticleSkeleton | TypeHelpCategorySkeleton | TypeHelpSubcategorySkeleton,
          'WITHOUT_UNRESOLVABLE_LINKS',
          string
        >
      | undefined,
    path: number[]
  ) => {
    if (!item) return null

    const isArticle = isTypeHelpArticle(item)
    const isCategory = isTypeHelpCategory(item)
    const isSubcategory = isTypeHelpSubcategory(item)

    return (
      <li key={item.sys.id}>
        {isArticle && item.fields.slug && <HelpLink slug={item.fields.slug}>{item.fields.title}</HelpLink>}
        {(isCategory || isSubcategory) && (
          <Disclosure
            heading={<span className="text-[15px] xl:text-lg ml-2">{item.fields.title}</span>}
            isOpen={isIndexOpen(path)}
            onClick={(isOpen) => disclosureClickHandler(path, isOpen)}
          >
            <ul className="pl-2">{item.fields.items.map((subItem, d2) => renderHelpLinks(subItem, [...path, d2]))}</ul>
          </Disclosure>
        )}
      </li>
    )
  }

  return (
    <nav className="text-indigoBlue dark:text-secondary-50 pb-12 lg:mb-4" ref={helpNavRef}>
      <div className="flex flex-col gap-1 mt-4 lg:mt-0 px-2">
        <HelpLink className="pl-2" slug={indexLink.slug}>
          {indexLink.title}
        </HelpLink>
        {helpNav.map((node, d0) => {
          if (!node) return null
          if (isTypeHelpNav(node)) {
            return (
              <ul key={`nav-toplnk-${node.sys.id}`} className="list-none">
                <li>
                  <Disclosure
                    heading={<span className="text-[15px] xl:text-lg">{node.fields.title}</span>}
                    isOpen={isIndexOpen([d0])}
                    onClick={(isOpen) => disclosureClickHandler([d0], isOpen)}
                  >
                    <ul className="pl-2">
                      {node?.fields?.items?.map((item, d1) => {
                        if (!item) return null
                        return renderHelpLinks(item, [d0, d1])
                      })}
                    </ul>
                  </Disclosure>
                </li>
              </ul>
            )
          }
          if (isTypeHelpArticle(node)) {
            return (
              <HelpLink key={node.sys.id} slug={node.fields.slug} indent={false}>
                {node.fields.title}
              </HelpLink>
            )
          }
        })}
      </div>
    </nav>
  )
}
